import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaCalendarAlt, FaTicketAlt, FaChevronLeft, FaChevronRight, FaTrophy } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://api.dreamtoday.in";

const NoImage = () => (
  <div className="w-full h-40 rounded-xl flex flex-col items-center justify-center
    bg-gradient-to-br from-yellow-100 to-amber-200 border border-yellow-200">
    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-300 to-amber-400
      flex items-center justify-center mb-1 shadow">
      <FaTrophy className="text-white text-base" />
    </div>
    <p className="text-xs text-yellow-700 font-medium">No Image</p>
  </div>
);

const SkeletonCard = () => (
  <div className="min-w-[280px] md:min-w-[320px] lg:min-w-[340px] rounded-2xl p-6
    border border-[#F5D77A] bg-gradient-to-br from-[#FFF9E6] via-[#FFF3CC] to-[#FFE8A3] animate-pulse">
    <div className="w-full h-40 rounded-xl bg-yellow-200 mb-5" />
    <div className="h-5 bg-yellow-200 rounded mb-2 w-3/4" />
    <div className="h-4 bg-yellow-100 rounded mb-4 w-1/2" />
    <div className="h-2 bg-yellow-200 rounded-full mb-5" />
    <div className="flex justify-between items-center">
      <div className="h-8 w-16 bg-yellow-200 rounded" />
      <div className="h-9 w-20 bg-yellow-300 rounded-xl" />
    </div>
  </div>
);

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-16 w-full">
    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-100 to-amber-200
      border border-yellow-300 flex items-center justify-center shadow mb-4">
      <FaTrophy className="text-yellow-400 text-3xl" />
    </div>
    <p className="text-yellow-800 font-semibold text-base mb-1">No Upcoming Campaigns</p>
    <p className="text-gray-400 text-sm">Stay tuned — exciting campaigns are coming soon!</p>
  </div>
);

const CampaignCard = ({ campaign, index }) => {
  const navigate = useNavigate();

  const ticketsSold =
    campaign.noOfTickets - (campaign.remainingTickets ?? campaign.noOfTickets);

  const progress = (ticketsSold / campaign.noOfTickets) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      className="min-w-[280px] md:min-w-[320px] lg:min-w-[340px]
        rounded-2xl p-6 border border-[#F5D77A]
        bg-gradient-to-br from-[#FFF9E6] via-[#FFF3CC] to-[#FFE8A3]
        shadow-[0_15px_35px_rgba(212,175,55,0.25)]
        hover:shadow-[0_20px_50px_rgba(212,175,55,0.45)]
        transition group"
    >
      <div className="relative mb-5">
        {campaign.images?.[0] ? (
          <img
            src={`${BASE_URL}${campaign.images[0]}`}
            alt={campaign.title}
            className="w-full h-40 object-cover rounded-xl"
          />
        ) : (
          <NoImage />
        )}
        <div className="absolute top-3 right-3">
          <span className="px-3 py-1 rounded-full text-xs font-semibold shadow-sm bg-blue-100 text-blue-700">
            UPCOMING
          </span>
        </div>
      </div>

      <h3 className="text-xl font-bold text-gray-900 mb-1">{campaign.title}</h3>

      <div className="flex items-center gap-2 text-gray-600 text-sm mb-4">
        <FaCalendarAlt className="text-yellow-700" />
        Starts: {new Date(campaign.startDate).toLocaleDateString("en-IN")}
      </div>

      <div className="flex justify-between text-sm mb-2">
        <span className="text-gray-600">Tickets Available</span>
        <span className="text-yellow-700 font-bold">{campaign.noOfTickets} total</span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-600">Price</p>
          <p className="text-xl font-bold text-yellow-700">₹{campaign.price}</p>
        </div>
        <button
          onClick={() => navigate(`/campaigns/${campaign._id}`)}
          className="px-5 py-2 rounded-xl font-semibold text-white
            bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700
            hover:scale-105 transition shadow-md flex items-center gap-2"
        >
          <FaTicketAlt /> Join Now
        </button>
      </div>
    </motion.div>
  );
};

let cachedData = null;
let fetchPromise = null;

const useCampaigns = () => {
  const [data, setData] = useState(cachedData);
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState("");

  useEffect(() => {
    if (cachedData) { setData(cachedData); setLoading(false); return; }
    if (!fetchPromise) {
      fetchPromise = fetch(`${BASE_URL}/api/admin/getcampaignsbytype`)
        .then((r) => r.json())
        .then((d) => {
          if (d.success) { cachedData = d.campaigns; return d.campaigns; }
          throw new Error(d.message || "Failed to load campaigns.");
        });
    }
    fetchPromise
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, []);

  return { data, loading, error };
};

const UpCommingCampaigns = () => {
  const scrollRef = useRef();
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);
  const { data, loading, error } = useCampaigns();
  const campaigns = data?.upcoming ?? [];

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeft(scrollLeft > 10);
    setShowRight(scrollLeft + clientWidth < scrollWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [campaigns]);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir === "left" ? -380 : 380, behavior: "smooth" });
  };

  if (!loading && !error && campaigns.length === 0) return null;

  return (
    <section className="py-24 bg-gradient-to-b from-white via-[#FFF7DB] to-white relative overflow-hidden">
      <div className="text-center mb-14 px-4">
        <h2 className="text-4xl md:text-5xl font-bold mb-3">
          <span className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 bg-clip-text text-transparent">
            UpComing Campaigns
          </span>
        </h2>
        <p className="text-gray-600">Be the first to join when they launch</p>
      </div>

      {showLeft && !loading && campaigns.length > 0 && (
        <button onClick={() => scroll("left")}
          className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-10
            bg-white/80 backdrop-blur-xl p-3 rounded-full border border-[#F5D77A] shadow-md">
          <FaChevronLeft className="text-yellow-700" />
        </button>
      )}
      {showRight && !loading && campaigns.length > 0 && (
        <button onClick={() => scroll("right")}
          className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-10
            bg-white/80 backdrop-blur-xl p-3 rounded-full border border-[#F5D77A] shadow-md">
          <FaChevronRight className="text-yellow-700" />
        </button>
      )}

      {!loading && error && (
        <div className="flex justify-center">
          <div className="px-6 py-4 rounded-2xl bg-red-50 border border-red-200 text-red-500 text-sm font-medium">
            ⚠️ {error}
          </div>
        </div>
      )}

      {loading && (
        <div className="flex gap-6 max-w-7xl mx-auto px-4 pb-6 overflow-hidden">
          {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
      )}

      {!loading && !error && campaigns.length === 0 && <EmptyState />}

      {!loading && !error && campaigns.length > 0 && (
        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto max-w-7xl mx-auto px-4 pb-6
            scrollbar-hide scroll-smooth touch-pan-x"
        >
          {campaigns.map((c, i) => (
            <CampaignCard key={c._id} campaign={c} index={i} />
          ))}
        </div>
      )}
    </section>
  );
};

export default UpCommingCampaigns;